import GoToCompanyButton from 'features/companies/components/GoToCompanyButton';
import { paths } from 'features/routing';
import { RoutingTabs } from 'features/ui';
import useUser from 'hooks/useUser';
import { useEffect, useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { capitalizeFirstChar } from 'utils/string';

type FeedDetailsNavbarProps = {
  purposeId: string;
  purposeSubcode: string;
  purposeCompanyId: string | null;
  purposeName: string;
};

const FeedDetailsNavbar = ({
  purposeId,
  purposeSubcode,
  purposeCompanyId,
  purposeName,
}: FeedDetailsNavbarProps) => {
  const { hasRole } = useUser();
  const routeMatch = useMatch(`/feeds/${purposeId}/:tab`);

  const canViewConnectTemplates = hasRole('Connect 1.0 (CRUD)');
  const canViewAuditLogs = hasRole('Audit Log Viewer', purposeSubcode);
  const canManagePurposes = hasRole('Manage Purposes');
  const canResetFeed = hasRole('Feed Reset');
  const canViewCompanyList = hasRole('View Companies List');

  const currentTab = useMemo(() => {
    return routeMatch?.params.tab ?? 'overview';
  }, [routeMatch]);

  useEffect(() => {
    const titlePrefix = `${purposeName} - `;
    const tabName = capitalizeFirstChar(String(routeMatch?.params.tab));
    document.title = titlePrefix + (tabName ?? 'Overview');
  }, [routeMatch, purposeName]);

  return (
    <RoutingTabs
      tabs={[
        {
          label: 'Overview',
          to: paths.feedDetailsOverview(purposeId),
        },
        {
          label: 'Images',
          to: paths.feedDetailsImageSettings(purposeId),
        },
        {
          label: 'Products',
          to: paths.feedDetailsProducts(purposeId),
        },
        {
          label: 'Connect Templates',
          show: canViewConnectTemplates,
          to: paths.feedDetailsConnectTemplates(purposeId),
        },
        {
          label: 'Audit Logs',
          show: canViewAuditLogs,
          to: paths.feedDetailsAuditLogs(purposeId),
        },
        {
          label: 'Purpose settings',
          show: canManagePurposes,
          to: paths.feedDetailsPurposeSettings(purposeId),
        },
        {
          label: 'Actions',
          show: canResetFeed,
          to: paths.feedDetailsActions(purposeId),
        },
      ]}
      value={currentTab}
    >
      {canViewCompanyList && purposeCompanyId && (
        <GoToCompanyButton companyId={purposeCompanyId} />
      )}
    </RoutingTabs>
  );
};

export default FeedDetailsNavbar;
